import {
  action,
  computed,
  makeObservable,
  observable,
  type ObservableMap,
  reaction,
  runInAction,
} from 'mobx'
import { type PostAssignmentPromptType } from '../firestore/PromptPostAssignmentType/types'
import {
  batchDeletePromptTestConversation,
  createPromptTestConversation,
  getPromptTestConversations,
} from '../firestore/SectionAssignmentConversationPromptTest'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SectionAssignmentConversationPromptTest } from '../models/SectionAssignmentConversationPromptTest'
import { PromptType, type StaticModelCollection } from '../types'
import { Cubit } from './core'
import { Section } from '../models/Section'
import { fetchSection } from '../firestore/Section'
import { SectionAssignment } from '../models/SectionAssignment'
import { fetchSectionAssignment } from '../firestore/SectionAssignment'
import { SlideDeck } from '../models/SlideDeck'
import { fetchSlideDeck } from '../firestore/SlideDeck'
import { getDocWithError } from '../firestore-mobx/fetch'
import { doc, writeBatch } from 'firebase/firestore'
import { RoomState } from '../models/RoomState'
import { fetchRoomStates } from '../firestore/RoomState'
import {
  batchDeletePromptTestConversationItem,
  createPromptTestConversationItem,
  fetchPromptTestConversationItems,
} from '../firestore/SectionAssignmentConversationItemPromptTest'

export class AdminPostAssignmentPromptTestingCubit extends Cubit {
  repository: FirebaseRepository
  subType: PostAssignmentPromptType
  promptVersionId: string

  _conversations: StaticModelCollection<SectionAssignmentConversationPromptTest>
  roomStates: StaticModelCollection<RoomState>

  @observable sections = observable.map<string, Section>()
  @observable assignments = observable.map<string, SectionAssignment>()
  @observable slideDecks = observable.map<string, SlideDeck>()

  @observable private selectedConversationGroup:
    | { sectionId: string; assignmentId: string }
    | undefined

  deletingConversations = observable.set<string>([])

  private conversationsAndDependentDataInitialized = observable.box(false)

  constructor(
    repository: FirebaseRepository,
    {
      promptType,
      promptVersionId,
    }: {
      promptType: PostAssignmentPromptType.transcriptSearch
      promptVersionId: string
    }
  ) {
    super()
    this.repository = repository
    this.subType = promptType
    this.promptVersionId = promptVersionId
    this.roomStates = RoomState.emptyCollection(repository)
    this._conversations =
      SectionAssignmentConversationPromptTest.emptyCollection(repository)

    makeObservable(this)
  }

  initialize(): void {
    // start the conversation items stream
    this.addStream(
      getPromptTestConversations(this.repository, {
        promptType: PromptType.postAssignment,
        subType: this.subType,
        promptVersionId: this.promptVersionId,
      }),
      (models) => {
        this._conversations.replaceModels(models)
        models.forEach(({ data: { sectionId, assignmentId } }) => {
          // init with empty then fetch and replace async
          if (sectionId && !this.sections.has(sectionId)) {
            this.sections = runInAction(() =>
              dupeMapAndAddProperty(this.sections, {
                key: sectionId,
                value: Section.empty(this.repository),
              })
            )
            fetchSection(this.repository, {
              sectionId,
            }).then((section) => {
              this.sections = runInAction(() =>
                dupeMapAndAddProperty(this.sections, {
                  key: sectionId,
                  value: section,
                })
              )
            })
          }

          if (sectionId && assignmentId) {
            const assignmentKey = getSectionAssignmentKey({
              sectionId,
              assignmentId,
            })
            if (this.assignments.has(assignmentKey)) return
            this.assignments = runInAction(() =>
              dupeMapAndAddProperty(this.assignments, {
                key: assignmentKey,
                value: SectionAssignment.empty(this.repository),
              })
            )
            fetchSectionAssignment(this.repository, {
              sectionId,
              assignmentId,
            }).then((assignment) => {
              if (
                !assignment ||
                assignment.isEmpty ||
                (!assignment.hasData && assignment.isLoaded)
              )
                return
              this.assignments = runInAction(() =>
                dupeMapAndAddProperty(this.assignments, {
                  key: assignmentKey,
                  value: assignment,
                })
              )
              const { slideDeckId } = assignment.data
              if (!slideDeckId || this.slideDecks.has(slideDeckId)) return
              this.slideDecks = runInAction(() =>
                dupeMapAndAddProperty(this.slideDecks, {
                  key: slideDeckId,
                  value: SlideDeck.empty(this.repository),
                })
              )
              fetchSlideDeck(this.repository, { slideDeckId }).then(
                (slideDeck) => {
                  this.slideDecks = runInAction(() =>
                    dupeMapAndAddProperty(this.slideDecks, {
                      key: slideDeckId,
                      value: slideDeck,
                    })
                  )
                }
              )
            })
          }
        })
      }
    )

    this.addReaction({
      whenThisChanges: () => this.selectedConversationGroup,
      thenRunThisCode: async () => {
        this.roomStates.replaceModels([])
        this.roomStates.changeLoadingState(true)
        if (!this.selectedConversationGroup) return
        const rooms = await fetchRoomStates(
          this.repository,
          this.selectedConversationGroup
        )
        this.roomStates.replaceModels(rooms)
      },
    })

    // add reaction to check if conversations resolve w/ dependent data
    const reactionDisposer = reaction(
      () => ({
        conversationsLoaded: this._conversations.isLoaded,
        conversations: this._conversations,
        sections: this.sections,
        assignments: this.assignments,
        slideDecks: this.slideDecks,
      }),
      ({ conversations, sections, assignments }) => {
        if (!conversations.isLoaded) return
        for (const convo of this._conversations.models) {
          const { sectionId, assignmentId } = convo.data
          if (!sectionId || !assignmentId) continue
          const section = sections.get(sectionId)
          const assignment = assignments.get(
            getSectionAssignmentKey({ sectionId, assignmentId })
          )
          const slideDeck = assignment?.data.slideDeckId
            ? this.slideDecks.get(assignment.data.slideDeckId)
            : undefined

          // dependent data is not loaded, wait for next reaction
          if (
            !section?.isLoaded ||
            !assignment?.isLoaded ||
            !slideDeck?.isLoaded
          )
            return
        }
        // all conversations have their dependent data loaded
        // set loaded to true and dispose self
        this.conversationsAndDependentDataInitialized.set(true)
        reactionDisposer()
      },
      { fireImmediately: true }
    )

    // add to disposers so we can cleanup if it never resolves
    this.disposers.push(reactionDisposer)
  }

  addConversation({
    sectionId,
    assignmentId,
  }: {
    sectionId: string
    assignmentId: string
  }) {
    return createPromptTestConversation(this.repository, {
      promptType: PromptType.postAssignment,
      subType: this.subType,
      promptVersionId: this.promptVersionId,
      userId: this.repository.uid,
      sectionId,
      assignmentId,
    })
  }

  @action
  setSelectedConversationGroup(
    params:
      | {
          sectionId: string
          assignmentId: string
        }
      | undefined
  ) {
    this.selectedConversationGroup = params
  }

  async checkAssignmentExists({
    sectionId,
    assignmentId,
  }: {
    sectionId: string
    assignmentId: string
  }) {
    const docRef = doc(
      this.repository.firestore,
      'section',
      sectionId,
      'assignment',
      assignmentId
    )
    const assignmentSnap = await getDocWithError(
      docRef,
      'PromptTestingCubitAssignmentCheckError'
    )
    return assignmentSnap.exists()
  }

  /// methods/getters for conversation view ///
  startNewConservation() {
    if (!this.selectedConversationGroup)
      throw new Error('No selected conversation group set')
    return this.addConversation(this.selectedConversationGroup)
  }

  async startNewConversationWithMessage(message: string) {
    if (!this.selectedConversationGroup)
      throw new Error('No selected conversation group set')
    const conversationId = await this.startNewConservation()
    await createPromptTestConversationItem(this.repository, {
      promptType: PromptType.postAssignment,
      subType: this.subType,
      promptVersionId: this.promptVersionId,
      sectionId: this.selectedConversationGroup.sectionId,
      assignmentId: this.selectedConversationGroup.assignmentId,
      conversationId,
      userId: this.repository.uid,
      role: 'user',
      userContent: message,
    })
    return conversationId
  }

  async deleteConversation(conversationId: string) {
    if (this.deletingConversations.has(conversationId)) return
    runInAction(() => this.deletingConversations.add(conversationId))

    // create write batch
    const batchLimit = 450

    let currentBatch = writeBatch(this.repository.firestore)
    let batchOperationCount = 0

    // add conversation delete to the batch
    batchDeletePromptTestConversation(this.repository, {
      batch: currentBatch,
      conversationId,
      promptType: PromptType.postAssignment,
      subType: this.subType,
      promptVersionId: this.promptVersionId,
    })
    batchOperationCount++

    try {
      // get all conversation items for the conversation
      const conversationItems = await fetchPromptTestConversationItems(
        this.repository,
        {
          conversationId,
          promptType: PromptType.postAssignment,
          subType: this.subType,
          promptVersionId: this.promptVersionId,
        }
      )

      // add delete for all conversation items to the batch
      for (const item of conversationItems) {
        batchDeletePromptTestConversationItem(this.repository, {
          batch: currentBatch,
          promptType: PromptType.postAssignment,
          subType: this.subType,
          promptVersionId: this.promptVersionId,
          conversationId,
          conversationItemId: item.id,
        })
        batchOperationCount++

        if (batchOperationCount >= batchLimit) {
          await currentBatch.commit()
          currentBatch = writeBatch(this.repository.firestore)
          batchOperationCount = 0
        }
      }

      // commit any remaining operations
      if (batchOperationCount > 0) await currentBatch.commit()
    } finally {
      runInAction(() => this.deletingConversations.delete(conversationId))
    }
  }

  @computed
  get conversations() {
    if (!this.selectedConversationGroup) {
      return this._conversations
    }

    const { sectionId, assignmentId } = this.selectedConversationGroup

    const models = this._conversations.models.filter(
      (conv) =>
        conv.data.sectionId === sectionId &&
        conv.data.assignmentId === assignmentId
    )

    const col = SectionAssignmentConversationPromptTest.emptyCollection(
      this.repository
    )
    col.replaceModels(models)
    return col
  }

  @computed
  get sectionId() {
    if (!this.selectedConversationGroup) return ''
    return this.selectedConversationGroup.sectionId
  }

  @computed
  get assignmentId() {
    if (!this.selectedConversationGroup) return ''
    return this.selectedConversationGroup.assignmentId
  }
  /// end methods/getters for conversation view ///

  @computed
  get conversationsGroupedByAssignment() {
    const map = new Map<
      string,
      {
        section: Section
        assignment: SectionAssignment
        slideDeck: SlideDeck
        conversations: SectionAssignmentConversationPromptTest[]
      }
    >()
    this._conversations.models.forEach((conversation) => {
      const { sectionId, assignmentId } = conversation.data
      if (!sectionId || !assignmentId) return

      const assignmentKey = getSectionAssignmentKey({ sectionId, assignmentId })
      let group = map.get(assignmentKey)
      if (!group) {
        const section = this.sections.get(sectionId)
        if (!section || section.isEmpty) return
        const assignment = this.assignments.get(assignmentKey)
        if (!assignment || assignment.isEmpty) return
        const slideDeck = assignment?.data.slideDeckId
          ? this.slideDecks.get(assignment.data.slideDeckId)
          : undefined
        if (!slideDeck || slideDeck.isEmpty) return
        group = { section, assignment, slideDeck, conversations: [] }
        map.set(assignmentKey, group)
      }
      group.conversations.push(conversation)
    })

    // sort the conversations by createdAt in descending order
    map.forEach((group) => {
      group.conversations.sort(
        (a, b) => b.data.createdAt.getTime() - a.data.createdAt.getTime()
      )
    })

    // return an iterable with the most recently created conversations on top
    return Array.from(map.values()).sort((a, b) => {
      const lastA = a.conversations[0]?.data.createdAt.getTime() || 0
      const lastB = b.conversations[0]?.data.createdAt.getTime() || 0
      return lastB - lastA
    })
  }

  @computed
  get ready() {
    return this.conversationsAndDependentDataInitialized.get()
  }
}

function getSectionAssignmentKey({
  sectionId,
  assignmentId,
}: {
  sectionId: string
  assignmentId: string
}) {
  return `${sectionId}-${assignmentId}`
}

function dupeMapAndAddProperty<T>(
  map: ObservableMap<string, T>,
  { key, value }: { key: string; value: T }
): ObservableMap<string, T> {
  // copy the map
  const newMap = observable.map<string, T>()
  map.forEach((v, k) => newMap.set(k, v))
  // add the new property
  newMap.set(key, value)
  return newMap
}
